import { useAuth0 } from "@auth0/auth0-react";
import { Spinner } from "@sg/ui";
import { ReactNode, Suspense, useMemo } from "react";
import { Navigate, generatePath, matchPath, useLocation, useParams, useRoutes } from "react-router-dom";
import { Sidebar } from "~/components/Layout/Sidebar";
import { useIsAuthorized } from "~/hooks/util/useRoles";
import { TradeSharePage } from "~/pages/Trade/TradeSharePage";
import useAuthStore from "~/store/auth";
import { RoleV2 } from "~/types";
import { ROUTE, ROUTE_CONFIG, RouteConfiguration } from "./config";

const ProtectedRoute = ({
  component,
  preserve,
  roles,
}: {
  component: ReactNode;
  preserve?: boolean;
  roles: RoleV2[];
}): JSX.Element => {
  const route = useCurrentRoute();
  const location = useLocation();
  const { isAuthenticated, isLoading, error } = useAuth0();
  const validRoutes = [ROUTE.AUTH_ERROR, ROUTE.LOGIN];
  const authorized = useIsAuthorized(roles);

  if (error && !validRoutes.includes(route as ROUTE)) {
    return <Navigate replace to={ROUTE.AUTH_ERROR} />;
  }

  if (!error && !isAuthenticated && !isLoading && route !== ROUTE.LOGIN) {
    return (
      <Navigate
        replace
        to={ROUTE.LOGIN}
        state={preserve ? { returnTo: `${location.pathname}${location.search}` } : {}}
      />
    );
  }

  if (!authorized) {
    return <Navigate replace to="/not-found" />;
  }

  return <>{component}</>;
};

const ApplicationRoutes = () => {
  function parseRouteConfiguration(data: RouteConfiguration[]) {
    return data.reduce((arr: RouteConfiguration[], item) => {
      if (item.children) item.children = parseRouteConfiguration(item.children);
      const result = { ...item };
      if (item.props?.roles) {
        result.element = <ProtectedRoute component={result.element} roles={item.props.roles} />;
      }

      arr.push(result);
      return arr;
    }, []);
  }

  const parsedConfig = useMemo(() => {
    return parseRouteConfiguration(ROUTE_CONFIG);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routes = useRoutes([
    ...parsedConfig,
    {
      path: "/share/options",
      element: <ProtectedRoute component={<TradeSharePage />} roles={[]} preserve />,
    },
    {
      path: "/share/forwards",
      element: <ProtectedRoute component={<TradeSharePage isForward />} roles={[]} preserve />,
    },
    {
      path: "/share/spreads",
      element: <ProtectedRoute component={<TradeSharePage isPackage />} roles={[]} preserve />,
    },
  ]);
  return (
    <Suspense
      fallback={
        <Sidebar>
          <div className="h-screen flex items-center justify-center">
            <Spinner className="h-full" size="xl" />
          </div>
        </Sidebar>
      }
    >
      {routes}
    </Suspense>
  );
};

const findRoute = (pathname: string) => {
  let result: RouteConfiguration | undefined;
  function iter(item: RouteConfiguration) {
    const match = item?.props?.id ? matchPath(item.props.id, pathname) : false;
    if (match) {
      result = item;
      return true;
    }
    return Array.isArray(item.children) && item.children.some(iter);
  }

  ROUTE_CONFIG.some(iter);
  return result;
};

// Returns the current registered route that the user is currently visiting.
const useCurrentRoute = (): ROUTE | undefined => {
  const { pathname } = useLocation();

  const route = findRoute(pathname);
  return route?.props?.id;
};

// Returns the current registered route config that the user is currently visiting.
const useCurrentRouteConfig = (): RouteConfiguration | null => {
  const { pathname } = useLocation();

  const route = findRoute(pathname);
  return route ? route : null;
};

const useIsCurrentRoute = (): ((url?: string) => boolean) => {
  const { path = "" } = useCurrentRouteConfig() || {};
  return (url) => !!url && path.split("?")[0] === url.split("?")[0];
};

const OrgContextFinder = ({ route, children }: { route: ROUTE; children?: ReactNode }) => {
  const { orgId } = useParams();
  const { org_id } = useAuthStore((state) => state.userInfo);

  const noOrgId = !orgId || orgId === ":orgId";

  if (noOrgId && org_id) {
    return (
      <Navigate
        replace
        to={generatePath(route, {
          orgId: org_id || "",
        })}
      />
    );
  }

  return <>{children}</>;
};

export { ApplicationRoutes, OrgContextFinder, findRoute, useCurrentRoute, useCurrentRouteConfig, useIsCurrentRoute };
